import { AppleFilled, GoogleOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import React from 'react';
import {
  buildAuthUrl,
  generateAndSaveCodeVerifier,
  generateAndSaveState,
  generateCodeChallenge,
  getLastUsedAuth,
  saveLastUsedAuth,
} from '../../routes/oauth_page';
import { LoginForm } from '../login/login_form';
import { OmniaButton } from '../omnia_button/omnia_button';
import { SignupForm } from '../signup/signup_form';

interface SignupLoginProps {
  defaultTab?: 'signup' | 'login';
  fromPath?: string;
  forwardRoute?: string;
}

export const LastUsedBadge = () => (
  <span className="absolute -top-2 -right-4 z-10 bg-green-400 rounded-full px-2 py-0.5 text-xs text-gray-500">
    Last used
  </span>
);

export const SignupLogin = (props: SignupLoginProps) => {
  const { defaultTab, fromPath, forwardRoute } = props;
  const [activeTab, setActiveTab] = React.useState<'signup' | 'login'>(defaultTab || 'signup');
  const lastUsedAuth = getLastUsedAuth();

  return (
    <div className="p-6 max-w-[480px] w-full">
      <h1 className="text-2xl mb-6">
        {activeTab === 'signup' ? 'Create your Omnia Account' : 'Welcome Back'}
      </h1>
      <nav className="mb-6">
        <ul className="grid grid-cols-2 p-1 bg-[#e0e4e3] rounded">
          <li className="flex">
            <button
              className={classNames('block w-full px-4 py-1.5 text-center', {
                'bg-white font-bold shadow rounded-sm': activeTab === 'signup',
              })}
              onClick={() => setActiveTab('signup')}
            >
              Create Account
            </button>
          </li>
          <li className="flex">
            <button
              className={classNames('block w-full px-4 py-1.5 text-center', {
                'bg-white font-bold shadow rounded-sm': activeTab === 'login',
              })}
              onClick={() => setActiveTab('login')}
            >
              Sign In
            </button>
          </li>
        </ul>
      </nav>
      <div>
        <SignupForm renderForm={activeTab === 'signup'} forwardRoute={forwardRoute} />
        <LoginForm
          renderForm={activeTab === 'login'}
          forwardRoute={forwardRoute}
          fromPath={fromPath}
        />
      </div>

      <div className="flex flex-col gap-4 pt-6">
        <OmniaButton
          block
          kind="tertiary"
          size="lg"
          className="relative"
          onClick={() => {
            const codeVerifier = generateAndSaveCodeVerifier();
            const state = generateAndSaveState({
              fromPath,
            });
            saveLastUsedAuth('SignInWithApple');
            generateCodeChallenge(codeVerifier).then((hash) => {
              const url = buildAuthUrl({
                provider: 'SignInWithApple',
                state,
                codeChallenge: hash,
              });
              window.location.href = url;
            });
          }}
        >
          <AppleFilled /> Continue with Apple
          {lastUsedAuth === 'SignInWithApple' && <LastUsedBadge />}
        </OmniaButton>

        <OmniaButton
          block
          kind="tertiary"
          size="lg"
          className="relative"
          onClick={() => {
            const codeVerifier = generateAndSaveCodeVerifier();
            const state = generateAndSaveState({
              fromPath,
            });
            saveLastUsedAuth('Google');
            generateCodeChallenge(codeVerifier).then((hash) => {
              const url = buildAuthUrl({
                provider: 'Google',
                state,
                codeChallenge: hash,
              });
              window.location.href = url;
            });
          }}
        >
          <GoogleOutlined /> Continue with Google
          {lastUsedAuth === 'Google' && <LastUsedBadge />}
        </OmniaButton>
      </div>
    </div>
  );
};
